import type { KeyboardEvent, MouseEvent } from 'react';
import { KIND_LABEL } from '../display';
import { agentType, runtimeTone } from '../domain/agentStatus';
import { launchLabel } from '../domain/plannedCard';
import LoopControls from './LoopControls';
import type { AgentCard as AgentCardModel } from '../types';
import type { LoopState } from '../../../apps/apis/commandCenter/types';

/** Splits 'success 99.4%' into its label and the emphasised value. */
const splitSuccess = (text: string): [string, string] => {
  const at = text.lastIndexOf(' ');
  return at === -1 ? ['', text] : [text.slice(0, at), text.slice(at + 1)];
};

interface AgentCardProps {
  card: AgentCardModel;
  code: string | null;
  onOpen: () => void;
  onCopyCode: (code: string) => void;
  /** Loop cards only: moving a loop is the one thing the board writes. */
  onLoopTransition?: ((loopId: string, toState: LoopState) => void) | undefined;
  onLoopRemove?: ((loopId: string) => void) | undefined;
  /** Opens the picker that names the skill a loop became, which turns two tiles into one. */
  onLoopConnect?: ((loopId: string) => void) | undefined;
  onLoopDisconnect?: ((loopId: string) => void) | undefined;
}

/** One agent tile: status, objective, runtime tags, code and today's run stats. */
const AgentCard = ({
  card,
  code,
  onOpen,
  onCopyCode,
  onLoopTransition,
  onLoopRemove,
  onLoopConnect,
  onLoopDisconnect,
}: AgentCardProps) => {
  // A tile whose id is not the loop's own is an agent tile carrying a plan: already connected.
  const wired = card.loop !== null && !card.id.startsWith('loop:');
  const kind = agentType(card);
  const [succLabel, succValue] = splitSuccess(card.succText);
  const launch = card.plan ? launchLabel(card.plan) : null;

  const stop = (e: MouseEvent, run: () => void) => {
    e.stopPropagation();
    run();
  };

  return (
    <div
      className={`agent${card.planned ? ' planned' : ''}${card.loop ? ` loop-${card.loop.state}` : ''}`}
      role="button"
      tabIndex={0}
      data-cat={card.section}
      data-open={card.id}
      title={[card.goal, card.sub, card.sched].filter(Boolean).join('\n')}
      onClick={onOpen}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onOpen();
        }
      }}
    >
      <div className="a-top">
        <span className={`a-dot ${card.dot}`} />
        <div>
          <div className="a-name">{card.name}</div>
        </div>
        {card.badge ? <span className={`a-badge ${card.badge.className}`}>{card.badge.text}</span> : null}
      </div>
      <div className="a-goal">
        {card.goalParts.map((part, i) => (part.bold ? <b key={i}>{part.text}</b> : <span key={i}>{part.text}</span>))}
      </div>
      <div className="a-sub">{card.sub}</div>
      <div className="a-tags">
        {card.tags.map((tag) => (
          <span key={tag.label} className={`tg ${tag.kind} tone-${runtimeTone(tag.kind)}`}>
            {tag.label}
          </span>
        ))}
      </div>
      <div className="a-runs">
        {code ? (
          <span
            className="a-code"
            role="button"
            tabIndex={-1}
            title={`Click to copy — ${code}`}
            onClick={(e) => stop(e, () => onCopyCode(code))}
          >
            {code}
          </span>
        ) : null}
        <span className="big tabular">{card.runsText}</span>
        <span className="lbl">
          {card.runsLabel}
          {launch ? <span className={`ln${launch.overdue ? ' ln-over' : ''}`}> · {launch.text}</span> : null}
        </span>
        <span className={`succ ${card.planned ? 'new' : ''}`}>
          {succLabel ? `${succLabel} ` : ''}
          <b className={card.succTone}>{succValue}</b>
        </span>
        <span className={`a-type ${kind}`}>{KIND_LABEL[kind]}</span>
      </div>
      {card.loop ? (
        <LoopControls
          card={card}
          wired={wired}
          onLoopTransition={onLoopTransition}
          onLoopRemove={onLoopRemove}
          onLoopConnect={onLoopConnect}
          onLoopDisconnect={onLoopDisconnect}
        />
      ) : null}
    </div>
  );
};

export default AgentCard;
